import React from 'react';
import { formatCurrency } from '../../utils/formatCurrency';

interface SensitivityAnalysisProps {
  data?: {
    parameter: string;
    results: {
      base_claim: number;
      range: {
        min: number;
        max: number;
        spread: number;
        spread_pct?: number;
      };
      results: Array<{ pct: number; claim: number }>;
    };
    run_at: string;
  };
  sensitivityParam: string;
  sensitivityLoading: boolean;
  onParamChange: (param: string) => void;
  onRunAnalysis: (param?: string) => void;
}

export const SensitivityAnalysisCard: React.FC<SensitivityAnalysisProps> = ({
  data,
  sensitivityParam,
  sensitivityLoading,
  onParamChange,
  onRunAnalysis,
}) => {
  const sa = data;
  const saBaseClaim = sa?.results?.base_claim || 0;
  const saMin = sa?.results?.range?.min || 0;
  const saMax = sa?.results?.range?.max || 0;
  const saSpread = sa?.results?.range?.spread || 0;
  const saSpreadPct = saBaseClaim > 0 ? (saSpread / saBaseClaim * 100) : 0;

  return (
    <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <span className="text-2xl">📈</span>
          Sensitivity Analysis
        </h2>
        {sa && (
          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">✓ Complete</span>
        )}
      </div>
      
      {/* Explanation */}
      <div className="bg-blue-50 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-700">
          <strong>What is Sensitivity Analysis?</strong> This test shows how your claim amount would change if key inputs (like payroll or revenue) were different by ±20%. It helps demonstrate the robustness of your claim — a smaller spread means your calculation is more stable and reliable.
        </p>
      </div>
      
      {sa ? (
        <div className="space-y-4">
          {/* Results Display */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Parameter Tested</p>
              <p className="text-lg font-bold text-blue-700">{sa.parameter?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Base Claim</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(saBaseClaim)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Spread</p>
              <p className="text-lg font-bold text-blue-700">{saSpreadPct.toFixed(1)}%</p>
            </div>
          </div>
          
          {/* Range visualization */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-3 font-medium">Claim Range (±20% variation)</p>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="text-xs text-gray-500">Min</p>
                <p className="text-sm font-bold text-blue-700">{formatCurrency(saMin)}</p>
              </div>
              <div className="flex-1 relative">
                <div className="h-4 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 rounded-full"></div>
                <div className="absolute left-1/2 top-0 w-1 h-4 bg-blue-800 rounded transform -translate-x-1/2" title="Base Claim"></div>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">Max</p>
                <p className="text-sm font-bold text-blue-700">{formatCurrency(saMax)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Total spread: {formatCurrency(saSpread)} • The center marker shows your base claim
            </p>
          </div>
          
          {/* Re-run buttons */}
          <div className="border-t pt-4">
            <p className="text-sm text-gray-600 mb-2">Test a different parameter:</p>
            <div className="flex gap-2">
              {[
                { key: 'payroll', label: 'Payroll' },
                { key: 'revenue', label: 'Revenue' },
                { key: 'variable_expenses', label: 'Expenses' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => onRunAnalysis(key)}
                  disabled={sensitivityLoading}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
                    sa.parameter === key 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  } disabled:opacity-50`}
                >
                  {sensitivityLoading && sensitivityParam === key ? '...' : label}
                </button>
              ))}
            </div>
          </div>
          
          <p className="text-xs text-gray-400">Last run: {new Date(sa.run_at).toLocaleString()}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select parameter to test:</label>
            <select
              value={sensitivityParam}
              onChange={(e) => onParamChange(e.target.value)}
              className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="payroll">Employee Payroll</option>
              <option value="revenue">Revenue</option>
              <option value="variable_expenses">Variable Expenses</option>
            </select>
          </div>
          <button
            onClick={() => onRunAnalysis()}
            disabled={sensitivityLoading}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {sensitivityLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Analyzing...
              </span>
            ) : 'Run Sensitivity Analysis'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SensitivityAnalysisCard;
